import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSnackbar } from 'ui-components';
import { FEATURE_FLAGS } from '~/constants';
import { trackEvent } from '~/core/utils/trackEvent';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';

import { FakeDoorButtonProps, createFakeDoorButton } from './components/Button';
import { FakeDoorModal } from './components/Modal';
import { EVENT_PREFIX } from './constants';
import { EventProperties } from './types';
import { generateHashString } from './utils';

interface FakeDoorContextType {
  action: React.FC<FakeDoorButtonProps> | null;
}

export const FakeDoorContext = createContext<FakeDoorContextType | undefined>(
  undefined,
);

interface FakeDoorProviderProps {
  actions: string[];
  children: ReactNode;
  eventProperties?: EventProperties;
  isEnabled?: boolean;
  pathname: string;
}

export const FakeDoorProvider: React.FC<FakeDoorProviderProps> = ({
  actions,
  children,
  isEnabled = false,
  pathname,
  eventProperties: defaultEventProperties = {},
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [eventProperties, setEventProperties] = useState<EventProperties>(
    defaultEventProperties,
  );

  const snackbar = useSnackbar();

  const { isEnabled: isFeatureEnabled } = useFeature(
    FEATURE_FLAGS.foamSharedItemCtaOption,
  );

  const action = useMemo(() => {
    if (!isFeatureEnabled || !isEnabled) return null;
    const index = generateHashString(pathname) % actions.length;
    return actions[index];
  }, [actions, isEnabled, pathname]);

  useEffect(() => {
    if (isInitialized || !isFeatureEnabled || !isEnabled || !action) return;

    trackEvent(EVENT_PREFIX + 'view', {
      action,
      ...defaultEventProperties,
      ...eventProperties,
    });

    setIsInitialized(true);
  }, [isInitialized, action, eventProperties, isEnabled, isFeatureEnabled]);

  const onOpen = () => {
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const handleActionClick = useCallback(
    (
      action: string,
      eventProperties: Record<string, string | number | boolean>,
    ) => {
      setEventProperties(eventProperties);
      trackEvent(EVENT_PREFIX + 'click', {
        action,
        ...defaultEventProperties,
        ...eventProperties,
      });
      onOpen();
    },
    [onOpen],
  );

  const handleModalClose = useCallback(() => {
    setEventProperties(defaultEventProperties);
    onClose();
  }, [onClose]);

  const handleModalSubmit = useCallback(
    async (email: string) => {
      if (!action) return;

      trackEvent(EVENT_PREFIX + 'submit', {
        action,
        email,
        ...defaultEventProperties,
        ...eventProperties,
      });

      snackbar.success({
        description: "We'll be in touch!",
      });
    },
    [action, eventProperties],
  );

  const value: FakeDoorContextType = {
    action: createFakeDoorButton({
      action,
      eventProperties: defaultEventProperties,
      onClick: handleActionClick,
    }),
  };

  return (
    <FakeDoorContext.Provider value={value}>
      {children}
      <FakeDoorModal
        isOpen={isOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
      />
    </FakeDoorContext.Provider>
  );
};

export const useFakeDoor = (): FakeDoorContextType => {
  const context = useContext(FakeDoorContext);

  if (context === undefined) {
    throw new Error('useFakeDoor must be used within a FakeDoorProvider');
  }

  return context;
};
